/* eslint-disable react/display-name */
import clsx from "clsx";
import { AnalystInfo } from "../analyst-card/analyst-info/AnalystInfo";
import { capitalizeFirstLetter } from "@/utils/helpers";
import { formatDecimalNumber } from '@/utils/numbers';
import styles from "./Table.module.scss";

export enum TableTypes {
    pivotPoints = "pivotPoints",
    movingAverages = "movingAverages",
    technicalIndicators = "technicalIndicators",
    analystForecasts = "analystForecasts",
}

export interface ITableHeader {
    accessor: string;
    label: string;
    translationKey?: string;
    align?: "left" | "right" | "center";
    renderValue?: (value) => string | JSX.Element;
    withToolTip?: boolean;
}

export type ITableConfig = Record<TableTypes, { columns: ITableHeader[]; title?: string; }>;

export const tablesConfig: ITableConfig = {
    [TableTypes.pivotPoints]: {
        columns: [
            { accessor: "name", label: "name", renderValue: rowData => rowData["name"] },
            {
                accessor: "s3",
                label: "s3",
                align: "center",
                renderValue: rowData => formatDecimalNumber(rowData["s3"]),
            },
            {
                accessor: "s2",
                label: "s2",
                align: "center",
                renderValue: rowData => formatDecimalNumber(rowData["s2"]),
            },
            {
                accessor: "s1",
                label: "s1",
                align: "center",
                renderValue: rowData => formatDecimalNumber(rowData["s1"]),
            },
            {
                accessor: "pivotPoints",
                label: "pivot points",
                align: "center",
                renderValue: rowData => formatDecimalNumber(rowData["pivotPoints"]),
            },
            {
                accessor: "r1",
                label: "r1",
                align: "center",
                renderValue: rowData => formatDecimalNumber(rowData["r1"]),
            },
            { accessor: "r2", label: "r2", align: "center", renderValue: rowData => formatDecimalNumber(rowData["r2"]), },
            { accessor: "r3", label: "r3", align: "right", renderValue: rowData => formatDecimalNumber(rowData["r3"]), },
        ],
    },
    [TableTypes.movingAverages]: {
        columns: [
            { accessor: "period", label: "Period", renderValue: rowData => rowData["period"] },
            {
                accessor: "simple",
                label: "Simple",
                align: "center",
                renderValue: rowData => (
                    <div className={clsx(styles.tableCellValue, styles.flexColumn)}>
                        {formatDecimalNumber(rowData["simple"])}
                        <div
                            className={clsx(null, {
                                [styles.buy]: rowData["action"] === "Buy",
                                [styles.sell]: rowData["action"] === "Sell",
                            })}>
                            {rowData["action"]}
                        </div>
                    </div>
                ),
            },
            {
                accessor: "exponential",
                label: "Exponential",
                align: "right",
                renderValue: rowData => (
                    <div className={clsx(styles.tableCellValue, styles.flexColumn)}>
                        {formatDecimalNumber(rowData["exponential"])}
                        <div
                            className={clsx(null, {
                                [styles.buy]: rowData["action"] === "Buy",
                                [styles.sell]: rowData["action"] === "Sell",
                            })}>
                            {rowData["action"]}
                        </div>
                    </div>
                ),
            },
        ],
    },
    [TableTypes.technicalIndicators]: {
        columns: [
            { accessor: "name", label: "name", renderValue: rowData => rowData["name"] },
            {
                accessor: "value",
                label: "Value",
                align: "center",
                renderValue: rowData => formatDecimalNumber(rowData["value"]),
            },
            {
                accessor: "action",
                label: "Implied Action",
                align: "right",
                renderValue: rowData => (
                    <div
                        className={clsx(styles.tableCellValue, {
                            [styles.buy]: rowData["action"] === "Buy",
                            [styles.sell]: rowData["action"] === "Sell",
                        })}>
                        {rowData["action"] || 'N/A'}
                    </div>
                ),
            },
        ],
    },
    [TableTypes.analystForecasts]: {
        columns: [
            {
                accessor: "analystName",
                label: "Analyst Name",
                translationKey: "analytics.analystForecasts.table.analystName",
                renderValue: rowData => {
                    const { name, rating, company, avatar } = rowData["analystInfo"];
                    return <AnalystInfo name={name} company={company} rating={rating} avatar={avatar} />;
                },
                withToolTip: true,
            },
            {
                accessor: "position",
                label: "Position",
                translationKey: "analytics.analystForecasts.table.position",
                align: "center",
                renderValue: rowData => (
                    <div
                        className={clsx(styles.tableCellValue, {
                            [styles.buy]: rowData["position"] === "Buy",
                            [styles.sell]: rowData["position"] === "Sell",
                        })}>
                        {rowData["position"]}
                    </div>
                ),
            },
            {
                accessor: "priceTarget",
                label: "Price Target",
                translationKey: "analytics.analystForecasts.table.priceTarget",
                align: "center",
                renderValue: rowData => (
                    <div
                        className={clsx(styles.tableCellValue, {
                            [styles.buy]: rowData["position"] === "Buy",
                            [styles.sell]: rowData["position"] === "Sell",
                        })}>
                        {rowData["priceTarget"]}
                    </div>
                ),
            },
            {
                accessor: "action",
                label: "Action",
                translationKey: "analytics.analystForecasts.table.action",
                align: "center",
                renderValue: rowData => capitalizeFirstLetter(rowData["action"]),
            },
            {
                accessor: "date",
                label: "Date",
                translationKey: "analytics.analystForecasts.table.date",
                align: "right",
                renderValue: rowData => rowData["date"],
            },
        ],
    },
};
